import { exhaustionPct } from "@agency/shared";
import { store } from "../store/GameStore.js";
import { button, el, formatSeconds } from "./dom.js";
import { RadioChip } from "./RadioChip.js";

export class Hud {
  private readonly root: HTMLElement;
  private readonly projectName: HTMLElement;
  private readonly projectFill: HTMLElement;
  private readonly projectPct: HTMLElement;
  private readonly projectWork: HTMLElement;
  private readonly busyLabel: HTMLElement;
  private readonly exhaustionFill: HTMLElement;
  private readonly exhaustionPct: HTMLElement;
  private readonly activityChip: HTMLElement;
  private readonly contribution: HTMLElement;
  private readonly online: HTMLElement;
  private readonly onLeaderboard: () => void;
  readonly radioChip: RadioChip;

  constructor(parent: HTMLElement, onLeaderboard: () => void) {
    this.onLeaderboard = onLeaderboard;
    this.root = el("div", "hud hidden");

    const topBar = el("div", "panel hud-bar");

    this.projectName = el("div", "hud-project-name", "Waiting for a project…");

    const projectBar = el("div", "bar hud-progress");
    this.projectFill = el("div", "fill");
    projectBar.appendChild(this.projectFill);

    const stats = el("div", "hud-stats");
    this.projectPct = el("span", "hud-stat hud-stat-strong", "0.0%");
    this.projectWork = el("span", "hud-stat", "0:00 / 0:00");
    this.busyLabel = el("span", "hud-stat hud-stat-desks", "0/8 desks");
    stats.append(this.projectPct, this.projectWork, this.busyLabel);

    const session = el("div", "hud-session");
    const onlineWrap = el("span", "hud-online");
    this.online = el("span", undefined, "0 online");
    onlineWrap.append(el("span", "hud-online-dot"), this.online);
    const lbBtn = button("🏆 Leaderboard ", "hud-btn hud-lb-btn");
    lbBtn.appendChild(el("kbd", undefined, "Tab"));
    lbBtn.addEventListener("click", () => this.onLeaderboard());
    this.radioChip = new RadioChip(this.root);
    session.append(this.radioChip.element, onlineWrap, lbBtn);

    topBar.append(this.projectName, projectBar, stats, session);

    const exhaustion = el("div", "panel hud-exhaustion");
    const exHeader = el("div", "hud-card-header");
    this.exhaustionPct = el("span", "hud-card-value", "0%");
    exHeader.append(el("span", "hud-card-title", "Exhaustion"), this.exhaustionPct);
    const exBar = el("div", "bar exhaustion-bar");
    this.exhaustionFill = el("div", "fill");
    exBar.appendChild(this.exhaustionFill);
    const exFooter = el("div", "hud-card-footer");
    this.activityChip = el("span", "activity-chip", "Idle");
    this.contribution = el("span", "server-line", "This project: 0:00");
    exFooter.append(this.activityChip, this.contribution);
    exhaustion.append(exHeader, exBar, exFooter);

    this.root.append(topBar, exhaustion);
    parent.appendChild(this.root);
  }

  mount(): void {
    store.on("project", (project) => this.updateProject(project));
    store.on("phase", () => this.refresh());
    this.radioChip.mount();
    setInterval(() => this.refresh(), 120);
    this.updateProject(store.project);
    this.refresh();
  }

  show(): void {
    this.root.classList.remove("hidden");
  }

  hide(): void {
    this.radioChip.close();
    this.root.classList.add("hidden");
  }

  private updateProject(project: {
    name: string;
    completedUnits: number;
    requiredUnits: number;
  }): void {
    this.projectName.textContent = project.name || "Waiting for a project…";
    this.projectName.title = this.projectName.textContent;
    const pct =
      project.requiredUnits > 0 ? (project.completedUnits / project.requiredUnits) * 100 : 0;
    this.projectFill.style.width = `${pct.toFixed(1)}%`;
    this.projectPct.textContent = `${pct.toFixed(1)}%`;
    this.projectWork.textContent = `${formatSeconds(project.completedUnits)} / ${formatSeconds(project.requiredUnits)}`;
  }

  private refresh(): void {
    const local = store.players.get(store.sessionId);
    const pct = local ? exhaustionPct(local.exhaustion) : 0;
    this.exhaustionFill.style.width = `${pct.toFixed(1)}%`;
    this.exhaustionFill.style.background =
      pct >= 80 ? "var(--bad)" : pct >= 50 ? "var(--warn)" : "var(--good)";
    this.exhaustionPct.textContent = `${Math.round(pct)}%`;
    this.activityChip.textContent = activityLabel(local?.activity, local?.burnedOut);
    this.contribution.textContent = `This project: ${formatSeconds(local?.workSeconds ?? 0)}`;

    this.online.textContent = `${store.countOnline()} online`;
    let occupied = 0;
    for (const occupant of store.desks.values()) if (occupant) occupied++;
    this.busyLabel.textContent = `💻 ${occupied}/${store.desks.size || 8} desks`;
  }
}

function activityLabel(activity: string | undefined, burnedOut: boolean | undefined): string {
  if (burnedOut) return "Burned out 🔥";
  if (activity === "working") return "Working 💻";
  if (activity === "coffee") return "Drinking coffee ☕";
  return "Idle";
}
